//Libs
import { useForm, SubmitHandler, Controller } from "react-hook-form";

//Local
import ColumnDialog from "./ColumnDialog";
import BaseInputCheckbox from "./BaseInputCheckbox";
import { ExistingCraftRecord } from "../models/craft-record";
import { ExistingVehicle } from "../models/vehicle";
import { ExistingSiteKeyLocation } from "../models/site-key-location";
import { CraftTypes } from "../models/craft-types";

interface Props {
  // DIALOG BASICS
  isOpen: boolean;
  onClose: () => void;
  vehicleList: ExistingVehicle[];
  additionalColumnsSelection: Record<string, any>;
  workRecordTableColumnsSelection: Record<string, boolean>;
  siteKeyLocationList: ExistingSiteKeyLocation[];
  validCraftTypes: number[] | undefined;
  setWorkRecordTableColumnsSelection: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  getLocationTitle: (id: string) => string;
  goToWorkRecordAndTasksPage?: (
    craftRecordID: ExistingCraftRecord["id"],
  ) => void;
}

export default function WorkRecordTableColumnsDialog(props: Props) {
  const { control, handleSubmit } = useForm<Record<string, any>>({
    defaultValues: props.workRecordTableColumnsSelection,
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<Record<string, any>> = (formValues) => {
    const { craftDetails, timestampMarkedInstalled, ...rest } = formValues;

    // REFACTOR:
    const extendedColumnDefaultValues = Object.keys(
      props.additionalColumnsSelection,
    ).reduce((acc, columnName) => {
      const column = columnName.split(".")[1];
      return {
        ...acc,
        [`${columnName}`]: craftDetails[column],
      };
    }, {});

    const columnSelection = {
      ...rest,
      "craftDetails.timestampMarkedInstalled": timestampMarkedInstalled, // could be undefined TODO:
      ...extendedColumnDefaultValues,
    };

    props.setWorkRecordTableColumnsSelection(columnSelection);
  };

  return (
    <ColumnDialog isOpen={props.isOpen} onClose={props.onClose}>
      <form
        autoComplete="off"
        onBlur={handleSubmit(onSubmit)}
        className="flex flex-col space-y-8 p-8 text-lg"
      >
        <Controller
          control={control}
          name="thumbnailURL"
          render={({ field }) => {
            return (
              <BaseInputCheckbox
                label="Photo"
                {...field}
                value={undefined} // don't need a value for this checkbox, just true or false.
                checked={field.value ?? false}
                onChange={(event) => field.onChange(event.target.checked)}
              />
            );
          }}
        />

        <Controller
          control={control}
          name="title"
          render={({ field }) => {
            return (
              <BaseInputCheckbox
                label="Title"
                {...field}
                value={undefined} // don't need a value for this checkbox, just true or false.
                checked={field.value ?? false}
                onChange={(event) => field.onChange(event.target.checked)}
              />
            );
          }}
        />

        {props.siteKeyLocationList.length >= 1 ? (
          <Controller
            control={control}
            name="locationID"
            render={({ field }) => {
              return (
                <BaseInputCheckbox
                  label="Location"
                  {...field}
                  value={undefined} // don't need a value for this checkbox, just true or false.
                  checked={field.value ?? false}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
              );
            }}
          />
        ) : null}

        <Controller
          control={control}
          name="numOpenTasks"
          render={({ field }) => {
            return (
              <BaseInputCheckbox
                label="Open Tasks"
                {...field}
                value={undefined} // don't need a value for this checkbox, just true or false.
                checked={field.value ?? false}
                onChange={(event) => field.onChange(event.target.checked)}
              />
            );
          }}
        />

        {props.validCraftTypes &&
        props.validCraftTypes.includes(CraftTypes.SCAFFOLDING) ? (
          <Controller
            control={control}
            name="timestampMarkedInstalled"
            render={({ field }) => {
              return (
                <BaseInputCheckbox
                  label="# Days Standing"
                  {...field}
                  value={undefined} // don't need a value for this checkbox, just true or false.
                  checked={field.value ?? false}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
              );
            }}
          />
        ) : null}

        <Controller
          control={control}
          name="createdBy"
          render={({ field }) => {
            return (
              <BaseInputCheckbox
                label="Created By"
                {...field}
                value={undefined} // don't need a value for this checkbox, just true or false.
                checked={field.value ?? false}
                onChange={(event) => field.onChange(event.target.checked)}
              />
            );
          }}
        />

        <Controller
          control={control}
          name="timestampRecordCreated"
          render={({ field }) => {
            return (
              <BaseInputCheckbox
                label="Created"
                {...field}
                value={undefined} // don't need a value for this checkbox, just true or false.
                checked={field.value ?? false}
                onChange={(event) => field.onChange(event.target.checked)}
              />
            );
          }}
        />

        {props.vehicleList.length >= 1 ? (
          <Controller
            control={control}
            name="assetID"
            render={({ field }) => {
              return (
                <BaseInputCheckbox
                  label="Asset"
                  {...field}
                  value={undefined} // don't need a value for this checkbox, just true or false.
                  checked={field.value ?? false}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
              );
            }}
          />
        ) : null}

        {Object.entries(props.additionalColumnsSelection).map(
          ([columnName, columnDetails]) => {
            return (
              <Controller
                key={`craftDetails.${columnName}`}
                control={control}
                name={columnName}
                render={({ field }) => {
                  return (
                    <BaseInputCheckbox
                      label={columnDetails.title}
                      {...field}
                      value={undefined} // don't need a value for this checkbox, just true or false.
                      checked={field.value ?? false}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  );
                }}
              />
            );
          },
        )}
      </form>
    </ColumnDialog>
  );
}
